import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Menu,
  X,
  ClipboardList,
  ListTodo,
  FileText,
  Bot,
  Cpu,
  Mail,
  MessageSquare,
  Target,
  Palette,
  Search,
  PenTool,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { UserNav } from '../../../components/UserNav';
import { useAuth } from '../../../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';

// Grouped navigation structure with sections (YOUR CUSTOM DESIGN)
const navigationGroups = [
  {
    title: '', // Main group (no title)
    items: [
      { name: 'Dashboard', href: '/client/dashboard', icon: LayoutDashboard },
    ]
  },
  {
    title: 'Media & Planning',
    items: [
      { name: 'Creative Studio', href: '/client/creative-studio', icon: Palette },
      { name: 'Media Planning', href: '/client/media-planning', icon: Target },
      { name: 'My Plans', href: '/client/my-plans', icon: ClipboardList },
    ]
  },
  {
    title: 'Execution',
    items: [
      { name: 'All Activities', href: '/client/activities', icon: ListTodo },
      { name: 'All Tasks', href: '/client/all-tasks', icon: FileText },
    ]
  },
  {
    title: 'Intelligence',
    items: [
      { name: 'AI Tools', href: '/client/ai-tools', icon: Cpu },
      { name: 'AI Agents', href: '/client/ai-agents', icon: Bot },
      {
        name: 'SEO Tools',
        icon: Search,
        children: [
          { name: 'SEO Dashboard', href: '/client/seo', icon: Search },
          { name: 'Keyword Research', href: '/client/seo/keyword-research', icon: Search },
          { name: 'Blog Writing', href: '/client/blog', icon: PenTool },
        ]
      },
    ]
  },
  {
    title: 'Campaigns',
    items: [
      { name: 'Email Campaign', href: '/client/email-campaign', icon: Mail },
      { name: 'WhatsApp Campaign', href: '/client/whatsapp-campaign', icon: MessageSquare },
    ]
  }
];

export default function ClientSidebar() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const { user, profile } = useAuth();

  // Auto-expand SEO Tools if we're on an SEO-related page
  useEffect(() => {
    if (location.pathname.startsWith('/client/seo') || location.pathname.startsWith('/client/blog')) {
      setExpandedItems(prev => ({ ...prev, 'SEO Tools': true }));
    }
  }, [location.pathname]);

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  const NavigationList = () => (
    <nav className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent px-3 py-4">
      <div className="space-y-6">
        {navigationGroups.map((group, groupIndex) => (
          <div key={groupIndex}>
            {group.title && (
              <h3 className="px-3 mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                {group.title}
              </h3>
            )}
            <div className="space-y-1">
              {group.items.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isExpanded = expandedItems[item.name];
                const isActive = item.href && (location.pathname === item.href || location.pathname.startsWith(item.href + '/'));
                const isChildActive = hasChildren && item.children?.some(child =>
                  child.href && (location.pathname === child.href || location.pathname.startsWith(child.href + '/'))
                );

                return (
                  <div key={item.name}>
                    {hasChildren ? (
                      <>
                        <button
                          onClick={() => toggleExpanded(item.name)}
                          className={cn(
                            'group flex w-full items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                            isChildActive || isExpanded
                              ? 'bg-white/5 text-white'
                              : 'text-gray-300 hover:bg-white/5 hover:text-white'
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <item.icon className={cn(
                              'h-4 w-4 shrink-0 transition-colors',
                              isChildActive ? 'text-primary' : 'text-gray-400 group-hover:text-white'
                            )} />
                            <span className="truncate">{item.name}</span>
                          </div>
                          <ChevronDown className={cn(
                            'h-3.5 w-3.5 transition-transform shrink-0',
                            isExpanded ? 'rotate-180' : '',
                            isChildActive ? 'text-primary' : 'text-gray-400'
                          )} />
                        </button>
                        {isExpanded && (
                          <div className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-3">
                            {item.children?.map((child) => {
                              const isChildItemActive = child.href && (location.pathname === child.href || location.pathname.startsWith(child.href + '/'));
                              return (
                                <Link
                                  key={child.name}
                                  to={child.href!}
                                  onClick={handleLinkClick}
                                  className={cn(
                                    'group flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                                    isChildItemActive
                                      ? 'bg-primary/10 text-primary'
                                      : 'text-gray-300 hover:bg-white/5 hover:text-white'
                                  )}
                                >
                                  <div className="flex items-center gap-3">
                                    <child.icon className={cn(
                                      'h-3.5 w-3.5 shrink-0 transition-colors',
                                      isChildItemActive ? 'text-primary' : 'text-gray-400 group-hover:text-white'
                                    )} />
                                    <span className="truncate">{child.name}</span>
                                  </div>
                                  {isChildItemActive && (
                                    <ChevronRight className="h-3 w-3 text-primary shrink-0" />
                                  )}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item.href!}
                        onClick={handleLinkClick}
                        className={cn(
                          'group flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-gray-300 hover:bg-white/5 hover:text-white'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <item.icon className={cn(
                            'h-4 w-4 shrink-0 transition-colors',
                            isActive ? 'text-primary' : 'text-gray-400 group-hover:text-white'
                          )} />
                          <span className="truncate">{item.name}</span>
                        </div>
                        {isActive && (
                          <ChevronRight className="h-3.5 w-3.5 text-primary shrink-0" />
                        )}
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </nav>
  );

  const UserProfile = () => {
    const displayName = profile?.name || user?.user_metadata?.full_name || 'Client Profile';
    const initials = displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

    return (
      <Link
        to="/client/profile"
        className="border-t border-white/5 p-4 hover:bg-white/5 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9 border border-white/10">
            <AvatarImage src={profile?.profile_photo || user?.user_metadata?.avatar_url} alt={displayName} />
            <AvatarFallback className="bg-primary/20 text-primary text-xs font-bold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{displayName}</p>
            <p className="text-[10px] text-gray-400 truncate">View Profile</p>
          </div>
        </div>
      </Link>
    );
  };

  const logoUrl = "/CupShupLogo.png";

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-50 flex h-14 items-center justify-between gap-4 border-b border-gray-200 bg-white px-4 shadow-sm">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5 text-gray-700" />
            ) : (
              <Menu className="h-5 w-5 text-gray-700" />
            )}
          </Button>
          <Link to="/client/dashboard">
            <div className="relative h-8 w-8 flex items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-primary/20 to-blue-500/20 p-1">
              <img
                src={logoUrl}
                alt="CupShup"
                className="h-full w-full object-contain drop-shadow-[0_0_8px_rgba(0,169,121,0.3)]"
              />
            </div>
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <UserNav />
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}>
          <div
            className="fixed inset-y-0 left-0 w-72 bg-[#1d2121] shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-16 items-center gap-3 border-b border-white/5 px-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded flex items-center justify-center overflow-hidden">
                  <img src={logoUrl} alt="CupShup" className="h-full w-full object-contain" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">
                    CupShup
                  </h1>
                </div>
              </div>
            </div>
            <NavigationList />
            <UserProfile />
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-[#1d2121] border-r border-white/5">
          <div className="flex h-16 shrink-0 items-center gap-3 px-6 border-b border-white/5">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded flex items-center justify-center overflow-hidden">
                <img src={logoUrl} alt="CupShup" className="h-full w-full object-contain" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">
                  CupShup
                </h1>
              </div>
            </div>
          </div>
          <NavigationList />
          <UserProfile />
        </div>
      </div>
    </>
  );
}